"use client";

import { useState } from "react";
import { useToolUi } from "@/components/tools/tool-ui";
import { writeToClipboard } from "@/lib/clipboard";

const NATO_LETTERS: Record<string, string> = {
  A: "Alfa",
  B: "Bravo",
  C: "Charlie",
  D: "Delta",
  E: "Echo",
  F: "Foxtrot",
  G: "Golf",
  H: "Hotel",
  I: "India",
  J: "Juliett",
  K: "Kilo",
  L: "Lima",
  M: "Mike",
  N: "November",
  O: "Oscar",
  P: "Papa",
  Q: "Quebec",
  R: "Romeo",
  S: "Sierra",
  T: "Tango",
  U: "Uniform",
  V: "Victor",
  W: "Whiskey",
  X: "X-ray",
  Y: "Yankee",
  Z: "Zulu",
};

const ICAO_DIGITS: Record<string, string> = {
  "0": "Zero",
  "1": "Wun",
  "2": "Too",
  "3": "Tree",
  "4": "Fower",
  "5": "Fife",
  "6": "Six",
  "7": "Seven",
  "8": "Ait",
  "9": "Niner",
};

const REFERENCE_CHARS = [
  ...Object.keys(NATO_LETTERS),
  ...Object.keys(ICAO_DIGITS),
];

const WHITESPACE_REGEX = /\s+/;
const DIGIT_REGEX = /[0-9]/;

function convertChar(char: string, plainDigits: boolean): string {
  const upper = char.toUpperCase();
  const letter = NATO_LETTERS[upper];
  if (letter !== undefined) {
    return letter;
  }
  if (DIGIT_REGEX.test(upper)) {
    if (plainDigits) {
      return upper;
    }
    return ICAO_DIGITS[upper];
  }
  return `"${char}"`;
}

function convertWord(word: string, plainDigits: boolean): string {
  const parts: string[] = [];
  for (const char of word) {
    parts.push(convertChar(char, plainDigits));
  }
  return parts.join(" ");
}

function convertText(
  text: string,
  plainDigits: boolean,
  linePerWord: boolean
): string {
  const words = text
    .trim()
    .split(WHITESPACE_REGEX)
    .filter((word) => word.length > 0);
  const converted = words.map((word) => convertWord(word, plainDigits));
  if (linePerWord) {
    return converted.join("\n");
  }
  return converted.join(" / ");
}

function referenceWord(char: string): string {
  return NATO_LETTERS[char] ?? ICAO_DIGITS[char];
}

/** English strings. A translated content file overrides these through `ui`. */
export const NATO_PHONETIC_CONVERTER_STRINGS = {
  options: "Options",
  plainDigitsLabel: "Plain digits (0-9 instead of Wun, Too, Tree…)",
  linePerWordLabel:
    'One line per word (unchecked: inline with "/" between words)',
  textToSpell: "Text to spell",
  inputPlaceholder: "Booking ref AB12",
  natoSpelling: "NATO spelling",
  passThroughNote:
    "Characters without a code word (punctuation and symbols) are passed through in quotes.",
  copied: "Copied!",
  copyResult: "Copy Result",
  clear: "Clear",
  chartHeading: "NATO Phonetic Alphabet Chart",
  chartFootnote:
    'Digit words shown are the official ICAO pronunciations (Niner avoids confusion with the German "nein"; Tree and Fife survive noisy radio better than "three" and "five").',
} as const;

export default function NatoPhoneticConverterWidget() {
  const [input, setInput] = useState("");
  const [plainDigits, setPlainDigits] = useState(false);
  const [linePerWord, setLinePerWord] = useState(true);
  const [copied, setCopied] = useState(false);

  const strings = useToolUi(NATO_PHONETIC_CONVERTER_STRINGS);

  const output =
    input.trim().length === 0
      ? ""
      : convertText(input, plainDigits, linePerWord);

  function handleCopy() {
    if (output.length === 0) {
      return;
    }
    writeToClipboard(output).then((copied) => {
      if (!copied) {
        return;
      }

      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  function handleClear() {
    setInput("");
    setCopied(false);
  }

  return (
    <>
      {/* Options */}
      <section className="space-y-3">
        <p className="font-medium text-foreground text-sm">{strings.options}</p>
        <div className="flex flex-wrap gap-x-6 gap-y-2">
          <label className="flex cursor-pointer items-center gap-2 text-foreground text-sm">
            <input
              checked={plainDigits}
              className="accent-primary"
              onChange={(e) => setPlainDigits(e.target.checked)}
              type="checkbox"
            />
            {strings.plainDigitsLabel}
          </label>
          <label className="flex cursor-pointer items-center gap-2 text-foreground text-sm">
            <input
              checked={linePerWord}
              className="accent-primary"
              onChange={(e) => setLinePerWord(e.target.checked)}
              type="checkbox"
            />
            {strings.linePerWordLabel}
          </label>
        </div>
      </section>

      {/* Input */}
      <div className="space-y-2">
        <label
          className="font-medium text-foreground text-sm"
          htmlFor="nato-input"
        >
          {strings.textToSpell}
        </label>
        <textarea
          className="min-h-[120px] w-full resize-y rounded-lg border border-border bg-card p-4 font-mono text-foreground text-sm leading-relaxed placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          id="nato-input"
          onChange={(e) => {
            setInput(e.target.value);
            setCopied(false);
          }}
          placeholder={strings.inputPlaceholder}
          value={input}
        />
      </div>

      {/* Output */}
      {output.length > 0 && (
        <div className="space-y-2">
          <label
            className="font-medium text-foreground text-sm"
            htmlFor="nato-output"
          >
            {strings.natoSpelling}
          </label>
          <textarea
            className="min-h-[140px] w-full resize-y rounded-lg border border-border bg-card p-4 font-mono text-foreground text-sm leading-relaxed focus:outline-none focus:ring-2 focus:ring-primary"
            id="nato-output"
            readOnly
            value={output}
          />
          <p className="text-muted-foreground text-xs">
            {strings.passThroughNote}
          </p>
        </div>
      )}

      {/* Actions */}
      <div className="flex flex-wrap gap-3">
        <button
          className="rounded-lg bg-primary px-5 py-2 font-medium text-primary-foreground text-sm transition-opacity hover:opacity-90 disabled:opacity-40"
          disabled={output.length === 0}
          onClick={handleCopy}
          type="button"
        >
          {copied ? strings.copied : strings.copyResult}
        </button>
        <button
          className="rounded-lg border border-border bg-card px-5 py-2 font-medium text-foreground text-sm transition-colors hover:bg-muted disabled:opacity-40"
          disabled={input.length === 0}
          onClick={handleClear}
          type="button"
        >
          {strings.clear}
        </button>
      </div>

      {/* Reference chart */}
      <section className="space-y-4">
        <h2 className="font-semibold text-foreground text-xl">
          {strings.chartHeading}
        </h2>
        <div className="grid grid-cols-3 gap-2 sm:grid-cols-4 md:grid-cols-6">
          {REFERENCE_CHARS.map((char) => (
            <div
              className="rounded-lg border border-border bg-card p-3 text-center"
              key={char}
            >
              <p className="font-semibold text-foreground">{char}</p>
              <p className="mt-1 text-muted-foreground text-xs">
                {referenceWord(char)}
              </p>
            </div>
          ))}
        </div>
        <p className="text-muted-foreground text-xs">{strings.chartFootnote}</p>
      </section>
    </>
  );
}
